import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { DataContext } from "../dataContext";
import DoubleIcon from "../components/DoubleIcon";
import TransferDetails from "../components/TransferDetails";
import SendSms, { clickSendSms } from "../components/SendSMS";
import { ReactComponent as Check } from "../assets/ico_check.svg";
import { ReactComponent as Info } from "../assets/ico_info.svg";
import { ReactComponent as Copy } from "../assets/ico_copy.svg";
import BottomButton from "../components/BottomButton";
import { isSMSCapable } from "../utils/utils";
import { useTransferApi } from "../api/transferApi";

const TransferSuccessful = ({ branding, homePath }) => {
  const navigate = useNavigate();
  const { history, notificationFeed } = useContext(DataContext);
  const urlParams = useParams();
  const [resendData, setResendData] = useState({});
  const hasSentSms = useRef(false);

  // RfP rows that haven't moved money yet are intentionally excluded from
  // history (they live on the PaymentRequests surface). For the requester
  // arriving here straight after creating an off-network request, history
  // won't have the row — so we also fetch via the per-transfer detail
  // endpoint and fall back to that.
  const { getTransferStatus } = useTransferApi();
  const [directTransfer, setDirectTransfer] = useState(null);
  const [directShortlink, setDirectShortlink] = useState(null);
  const [directFi, setDirectFi] = useState(null);

  const transfer =
    history?.find(({ id }) => id === urlParams.id) || directTransfer;
  const feedNotification = notificationFeed?.find(
    ({ transfer_id }) => transfer_id === urlParams.id
  );
  const isRequestForPay = transfer?.originated_as_rfp === true;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const tryLoad = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getTransferStatus(urlParams.id);
        if (cancelled) return;
        // Always stash the row so the page renders even when history hasn't
        // refreshed yet.
        setDirectTransfer(data);
        const events = data.events || data.getstream_events || [];
        const evt = events.find(
          (e) => e.event === "shortlink_generated" || e.type === "shortlink_generated"
        );
        const url = evt?.url || evt?.data?.shortlink || null;
        if (url) {
          setDirectShortlink(url);
          setDirectFi(
            data.financial_institution || evt?.data?.financial_institution || null
          );
          return;
        }
      } catch (e) {
        // Ignore — we retry a few times.
      }
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(tryLoad, 2000);
      }
    };
    tryLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.id]);

  // Synthesized notification — prefers the direct fetch so the page renders
  // data immediately on arrival. Shape matches what the existing code expects.
  const notification = feedNotification || (directShortlink ? {
    transfer_id: urlParams.id,
    shortlink: directShortlink,
    financial_institution: directFi,
    status: "pending",
  } : null);

  // notification/feed doesn't work locally, uncomment an option below

  // // ON NETWORK
  // const notification = notificationFeed.find(
  //   ({ transfer_id }) => transfer_id === "17bfb8a4-2fa3-5615-b4b4-e3cc6151c65d"
  // );

  // const transfer = {
  //   id: urlParams.id,
  //   on_network: true,
  //   status: notification?.status?.toUpperCase(),
  // };

  // // OFF NETWORK
  // const notification = notificationFeed.find(
  //   ({ transfer_id }) => transfer_id === "c35bc840-1865-5bf5-916d-b85ce20589c1"
  // );

  // const transfer = {
  //   amount: notification?.amount,
  //   id: urlParams.id,
  //   note: "Dummy data OFF network succeeded",
  //   on_network: false,
  //   // recipient: { type: "phone", value: "+16155551234" },
  //   recipient: { type: "email", value: "dummy@test.com" },
  //   sender_name: "Chet Wallsworth",
  //   status: "PENDING",
  //   timestamp: "2024-05-21T15:48:54.181114",
  // };
  const canSendSMS = isSMSCapable();

  const shouldShowSmsSend = () => {
    if (transfer) {
      return (
        transfer.recipient?.type === "phone" &&
        canSendSMS &&
        (notification?.shortlink ||
          (notification?.status === "succeeded" &&
            branding?.send_on_network_notification))
      );
    }
    return false;
  };

  useEffect(() => {
    if (isRequestForPay && notification?.shortlink) {
      // RfP off-network: user is asking the other party to pay them.
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `I am requesting $${transfer?.amount?.value} from you${notification?.financial_institution ? ` through my account at ${notification.financial_institution}` : ""}${transfer?.note ? ` with the note "${transfer.note}"` : ""}. Click this link to pay: ${notification.shortlink}`,
      });
    } else if (notification?.shortlink) {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding?.sms_customization?.off_net_first || "I am sending you"} $${transfer?.amount?.value} ${branding?.sms_customization?.off_net_second || "from my account at"} ${notification?.financial_institution || ""} ${branding?.sms_customization?.off_net_third || "with the note"} "${transfer?.note || ""}". ${branding?.sms_customization?.off_net_fourth || "Click this link to receive your funds:"} ${notification?.shortlink}`,
      });
    } else if (notification?.status === "succeeded") {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding?.sms_customization?.on_net_first || "I am sending you"} $${transfer?.amount?.value} ${branding?.sms_customization?.on_net_second || "with the note"} "${transfer?.note || ""}". ${branding?.sms_customization?.on_net_third || "The funds will be available shortly, and visible via online banking."}`,
      });
    }
  }, [branding, notification, transfer, isRequestForPay]);

  useEffect(() => {
    // Only attempt to send SMS once, and only when we have all the necessary data
    // Critical: Check resendData.message exists to prevent race condition with the previous useEffect
    if (shouldShowSmsSend() && !hasSentSms.current && resendData.message && resendData.identifier) {
      // Add a small delay to ensure the DOM has updated with the instructions
      const timeoutId = setTimeout(() => {
        hasSentSms.current = true;

        try {
          if (notification?.shortlink) {
            clickSendSms(
              resendData.message,
              resendData.identifier,
              branding?.disable_android_sms
            );
          } else if (branding?.send_on_network_notification) {
            clickSendSms(
              resendData.message,
              resendData.identifier,
              branding?.disable_android_sms
            );
          } else {
            console.warn("sms data issue: missing shortlink or on_network_notification flag");
          }
        } catch (error) {
          console.error("Failed to trigger SMS:", error);
          // Reset flag so user can manually retry via button
          hasSentSms.current = false;
        }
      }, 100); // Small delay to ensure resendData is fully set and DOM is updated

      return () => clearTimeout(timeoutId);
    }
  }, [shouldShowSmsSend, resendData, notification?.shortlink, branding?.send_on_network_notification]);

  const clickCopy = () => {
    navigator.clipboard.writeText(resendData.message);
    document.getElementById("show_click_copy").style.display = "block";
    setTimeout(() => {
      document.getElementById("show_click_copy").style.display = "none";
    }, 1000);
  };

  const smallSuccessIcon = () => {
    return (
      <Check
        style={{
          height: "auto",
          width: "40px",
          margin: "-35px 0 0 60px",
          zIndex: 1,
          borderRadius: "20px",
        }}
        className="fill_sending_success_color stroke_sending_bg_primary bg_sending_bg_primary"
      />
    );
  };

  const { renderDoubleIcon } = DoubleIcon(smallSuccessIcon);

  const nonDetailScreen = () => {
    return (
      <div
        className="center_page"
        style={{
          height: "65%",
          flexDirection: "column",
        }}
      >
        <h1>
          {isRequestForPay
            ? transfer?.outgoing === false
              ? "Payment Sent"
              : "Request Sent"
            : "Success"}
        </h1>
        <div
          data-cy="transfer_processed_message"
          style={{ margin: "-15px 0 25px 0", fontSize: "16px" }}
        >
          {isRequestForPay
            ? transfer?.outgoing === false
              ? "Payment was sent"
              : "Payment request sent"
            : "Payment was sent"}
        </div>
        {renderDoubleIcon()}
      </div>
    );
  };

  const detailScreen = () => {
    return (
      <div>
        <h1
          data-cy="transfer_processed_message"
          style={{ margin: 0, lineHeight: 1.3 }}
        >
          {isRequestForPay ? (
            transfer.on_network ? (
              <> Your payment request has been sent!</>
            ) : (
              <> Your payment request is ready to be sent!</>
            )
          ) : transfer.on_network ? (
            <> Your transfer has been sent!</>
          ) : (
            <> Your transfer is ready to be sent!</>
          )}
        </h1>
        <div className="transfer_action_info" style={{ marginBottom: "15px" }}>
          <div style={{ marginRight: "10px" }}>
            <Info className="fill_sending_bg_primary stroke_sending_bg_secondary" />
          </div>
          {isRequestForPay && transfer.on_network ? (
            <>
              Have your requestee log in to their sending experience to accept
              the request for payment.
            </>
          ) : isRequestForPay ? (
            transfer.recipient?.type === "phone" ? (
              <>
                Please text ("SMS") the payment request below so they can pay
                you...
              </>
            ) : (
              <>A payment request has been sent to their email</>
            )
          ) : transfer.recipient?.type === "phone" ? (
            <>
              Please text ("SMS") the instructions below to the recipient to
              claim funds...
            </>
          ) : (
            <>Instructions have been sent to recipient's email</>
          )}
        </div>
        {/* Skip the message / SMS / QR block for on-network RfP — the
            requestee gets an in-app notification + approval screen, nothing
            to text. */}
        {!(isRequestForPay && transfer.on_network) && (
          <>
            <h3 style={{ marginTop: "20px" }}>
              {isRequestForPay ? "Message to your requestee" : "Instructions for your recipient"}
            </h3>
            <div
              className="status"
              style={{
                marginBottom: "15px",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
              }}
            >
              <span>
                {notification?.shortlink ? (
                  <>
                    {resendData.message?.split("http")[0]}
                    <strong style={{ textDecoration: "underline" }}>
                      {notification.shortlink}
                    </strong>
                  </>
                ) : (
                  <>{resendData.message}</>
                )}
              </span>
              <div style={{ textAlign: "right" }}>
                <Copy
                  className="fill_sending_bg_tertiary stroke_sending_bg_secondary"
                  onClick={() => clickCopy()}
                  style={{ cursor: "pointer" }}
                />
                <div id="show_click_copy" style={{ display: "none" }}>
                  Message copied
                </div>
              </div>
            </div>
            {shouldShowSmsSend() && <SendSms {...resendData} disableAndroidSms={branding?.disable_android_sms}></SendSms>}
          </>
        )}
        {isRequestForPay && notification?.shortlink && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "24px",
            }}
            data-cy="request_qr_code"
          >
            <div
              style={{
                fontSize: "12px",
                marginBottom: "10px",
                color: "#666",
              }}
            >
              Or have them scan this QR code
            </div>
            <div
              style={{
                padding: "12px",
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            >
              <QRCode
                value={notification.shortlink}
                size={160}
                level="M"
              />
            </div>
          </div>
        )}
        <div style={{ marginTop: "40px" }}>{TransferDetails(transfer)}</div>
      </div>
    );
  };

  return (
    <main className="page_without_nav">
      {transfer?.status ? (
        <>
          {transfer.on_network && !branding.send_on_network_notification ? (
            nonDetailScreen()
          ) : (
            <div
              style={{
                paddingBottom: "20px",
              }}
            >
              {detailScreen()}
            </div>
          )}
        </>
      ) : (
        <div className="center_page">Transfer Not Found</div>
      )}
      {BottomButton(() => navigate(homePath), "Return Home")}
    </main>
  );
};

export default TransferSuccessful;
